import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/hooks/useI18n";
import { toast } from "@/hooks/useToast";

interface UseStripeReturnFeedbackOptions {
  /** Invocata solo al rientro da un pagamento riuscito (es. refetch della dashboard). */
  onSuccess?: () => void;
}

/**
 * Feedback al rientro da Stripe Checkout.
 *
 * Le due edge function di pagamento riportano il cliente sulla pagina da cui e' partito
 * con `?payment=success|cancelled`. Senza questo hook l'utente atterrerebbe su una pagina
 * identica a prima, senza sapere se il pagamento e' andato a buon fine.
 *
 * Il messaggio e' scritto dal punto di vista di chi paga: ci si arriva solo tornando da
 * Stripe Checkout, quindi chi lo legge e' il viaggiatore (o il cliente sulla proposta
 * pubblica), mai il TD che incassa.
 *
 * Dice "stiamo registrando il pagamento" e non "pagato": la riga in
 * `payments` la scrive il webhook, che puo' arrivare qualche secondo dopo il redirect —
 * e con i metodi ad addebito differito (SEPA) anche giorni dopo. Dichiarare qui l'esito
 * finale mostrerebbe un dato che il database ancora non ha.
 *
 * I parametri vengono rimossi dall'URL subito dopo, cosi' un refresh non ripropone il
 * messaggio.
 */
export const useStripeReturnFeedback = (options?: UseStripeReturnFeedbackOptions) => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const outcome = searchParams.get("payment");

  // Sotto StrictMode l'effetto gira due volte in dev: senza questo il toast e'
  // doppio, perche' la pulizia dei parametri e' asincrona rispetto al secondo giro.
  const handled = useRef(false);

  useEffect(() => {
    if (!outcome || handled.current) return;
    handled.current = true;

    if (outcome === "success") {
      toast({
        variant: "info",
        title: t("payments.stripeReturn.successTitle"),
        description: t("payments.stripeReturn.successDesc"),
      });
      options?.onSuccess?.();
    } else if (outcome === "cancelled") {
      toast({
        title: t("payments.stripeReturn.cancelledTitle"),
        description: t("payments.stripeReturn.cancelledDesc"),
      });
    }

    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);
};
